import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiClient } from '../../lib/api';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import AuthFloatingInput from '../../components/auth/AuthFloatingInput';
import { slideUp } from '@/lib/motion';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const codeFromState = location.state?.code || '';

  const [code, setCode] = useState<string[]>(() => {
    if (codeFromState && /^\d{6}$/.test(codeFromState)) {
      return codeFromState.split('');
    }
    return ['', '', '', '', '', ''];
  });
  const [codePreFilled, setCodePreFilled] = useState(Boolean(codeFromState && /^\d{6}$/.test(codeFromState)));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!email) {
      navigate('/auth/forgot-password');
    } else if (codePreFilled) {
      // Code already entered — focus the password field
      passwordRef.current?.focus();
    } else {
      inputRefs.current[0]?.focus();
    }
  }, [email, navigate, codePreFilled]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only process if it's exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      // Focus last input
      inputRefs.current[5]?.focus();
    }
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate code
    const codeValue = code.join('');
    if (codeValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Validate confirm password
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/reset-password', {
        email,
        code: codeValue,
        new_password: password,
      });

      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      const apiMessage = data?.message || data?.error;
      const fieldErrors = data?.errors as Record<string, string[]> | undefined;

      if (status === 400) {
        // Show specific field errors if available, otherwise the API message
        const specificError = fieldErrors?.code?.[0] || fieldErrors?.new_password?.[0];
        setError(specificError || apiMessage || 'Invalid or expired verification code. Please request a new one.');
      } else if (status === 429) {
        setError(apiMessage || 'Too many attempts. Please try again later.');
      } else {
        setError(apiMessage || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResendCode = async () => {
    setResending(true);
    setResent(false);
    setError('');
    try {
      await apiClient.post('/auth/forgot-password', { email });
      setResent(true);
      // Clear the pre-filled code so user can enter new one
      setCode(['', '', '', '', '', '']);
      setCodePreFilled(false);
      setTimeout(() => {
        setResent(false);
        inputRefs.current[0]?.focus();
      }, 2000);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 429) {
        setError(err.response?.data?.message || 'Too many requests. Please try again later.');
      } else {
        setResent(true);
        setTimeout(() => setResent(false), 2000);
      }
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <motion.div {...slideUp} className="text-center">
          {/* Success Icon */}
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              width="96"
              height="96"
              viewBox="0 0 96 96"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M80.476 16.0003C78.4762 14.0003 31.9318 51.833 28.2074 59.652C28.2074 59.652 28.052 88.901 33.4176 89.7454C36.3204 89.7454 41.6446 74.3352 42.7662 70.9898C42.9096 70.5622 43.1058 70.1856 43.3792 69.8268C55.0758 54.484 82.4528 17.9773 80.476 16.0003Z"
                className="fill-primary-700 dark:fill-primary-200"
              />
              <path
                d="M11.1672 39.0989C8.65667 40.8149 8.56069 44.0419 10.8482 46.0457C13.9083 48.726 19.2201 53.0631 28.207 59.6654C28.2071 59.6564 28.2071 59.6519 28.2071 59.6519C31.9315 51.8329 78.4759 14.0002 80.4757 16.0002C82.4525 17.9772 55.0755 54.4839 43.3789 69.8267C43.1055 70.1855 42.9093 70.5621 42.7659 70.9897C41.6843 74.2158 36.6946 88.6612 33.7369 89.6877C40.0619 88.4685 46.3589 83.4096 52.1943 76.4228C59.5974 81.3099 64.8374 84.457 68.0228 86.2839C70.2514 87.5621 72.9852 87.0873 74.1568 84.8009C80.9214 71.5993 88.5442 32.4781 89.978 11.9828C90.2092 8.67713 87.531 6.03095 84.2714 6.62737C63.9762 10.3409 25.0572 29.6039 11.1672 39.0989Z"
                className="fill-primary-500"
              />
              <path
                d="M33.4173 89.7453C33.4172 89.7451 33.4174 89.7455 33.4173 89.7453V89.7453Z"
                className="fill-primary-500"
              />
            </svg>
          </div>

          <h1 className="text-[26px] font-bold text-success-600 dark:text-success-400 mb-2">
            Password Reset Successful!
          </h1>
          <p className="text-sm text-text-secondary mb-6">
            Your password has been successfully reset. Redirecting you to login...
          </p>

          <Link
            to="/auth/login"
            className="inline-block h-12 px-8 rounded-lg bg-primary-500 font-medium text-sm hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors leading-[48px]"
          >
            Go to Login
          </Link>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
      {/* Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-primary-500/10 dark:bg-primary-500/20 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-primary-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-[26px] font-bold text-text-primary mb-2">
          Request Sent Successfully!
        </h1>
        <p className="text-sm text-text-secondary leading-relaxed">
          We&apos;ve sent a 6-digit confirmation email to your email. Please enter the code in the
          box below to verify your email.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Hidden email input for password managers (Apple Keychain, Chrome, 1Password, etc.)
            Must be a real <input> with autocomplete="username" inside the <form>
            so managers can associate the email with the new password. */}
        <input
          type="email"
          name="email"
          autoComplete="username"
          value={email}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
        />

        {/* Visible email display */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Email address
          </label>
          <div className="w-full h-[52px] px-4 flex items-center text-sm border border-border-primary/30 rounded-lg bg-neutral-50 dark:bg-neutral-800 text-text-secondary">
            {email}
          </div>
        </div>
        {/* Code Input */}
        <div>
          <div className="flex gap-2.5 justify-center mb-2">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => !codePreFilled && handleCodeChange(index, e.target.value)}
                onKeyDown={e => !codePreFilled && handleCodeKeyDown(index, e)}
                onPaste={index === 0 && !codePreFilled ? handleCodePaste : undefined}
                readOnly={codePreFilled}
                disabled={loading}
                className={`w-12 h-14 text-center text-xl font-semibold border-2 rounded-lg transition-colors focus:outline-none ${
                  codePreFilled
                    ? 'border-success-400 dark:border-success-600 bg-success-50 dark:bg-success-900/20'
                    : error
                      ? 'border-error-500 focus:border-error-500'
                      : 'border-border-primary/30 focus:border-primary-500'
                } ${codePreFilled ? 'cursor-default' : ''} bg-transparent text-text-primary disabled:opacity-50`}
              />
            ))}
          </div>
          {codePreFilled && (
            <p className="text-xs text-success-600 dark:text-success-400 text-center mt-1">
              Code verified
            </p>
          )}
        </div>

        {/* New Password Field */}
        <div>
          <AuthFloatingInput
            label="New password"
            ref={passwordRef}
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setError('');
            }}
            disabled={loading}
            endAdornment={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="p-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            }
          />
          <p className="mt-1.5 text-xs text-text-secondary">
            Use 8+ characters with uppercase, lowercase, and numbers
          </p>
        </div>

        {/* Confirm Password Field */}
        <AuthFloatingInput
          label="Confirm new password"
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={e => {
            setConfirmPassword(e.target.value);
            setError('');
          }}
          disabled={loading}
        />

        {/* Error Message — above submit button */}
        {error && (
          <div className="p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm">
            <p>{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-full bg-primary-500 font-bold text-[15px] hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Updating password...' : 'Update password'}
        </button>
      </form>

      {/* Resend Code */}
      <div className="text-center mt-5">
        {resent ? (
          <p className="text-sm text-success-600 dark:text-success-400 font-medium">
            Code sent! Check your inbox.
          </p>
        ) : (
          <p className="text-sm text-text-secondary">
            Don&apos;t have a code?{' '}
            <button
              onClick={handleResendCode}
              disabled={resending}
              className="text-primary-500 font-medium hover:underline disabled:opacity-50"
            >
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          </p>
        )}
      </div>

      {/* Back to Login */}
      <div className="text-center mt-3">
        <Link
          to="/auth/login"
          className="text-sm text-text-secondary hover:text-text-primary transition-colors inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Return to sign in
        </Link>
      </div>
      </motion.div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
